/**
 * Quota Check Hook
 *
 * React hook for checking user quota limits BEFORE performing actions.
 * Provides real-time feedback on remaining uploads, processing jobs, and storage.
 */

import { useState } from 'react';
import { useAuth } from '@/features/auth/contexts/AuthContext';

export interface QuotaCheckResult {
  allowed: boolean;
  remaining: number;
  limit: number;
}

export const useQuotaCheck = () => {
  const { subscription, usage, refreshUserData } = useAuth();
  const [checking, setChecking] = useState(false);

  /**
   * Check if user can perform any operation (upload OR processing)
   * For hourly plans: checks compute hours budget
   * For monthly plans: uses processing_jobs_count for quota tracking
   *
   * @returns QuotaCheckResult with allowed status and remaining quota
   */
  const canOperate = async (): Promise<QuotaCheckResult> => {
    setChecking(true);

    // IMPORTANT: Allow operations if subscription data is still loading
    // Only block if data is loaded AND limit is reached
    if (!subscription || !usage) {
      setChecking(false);
      console.warn('⚠️ Subscription data not loaded yet, allowing operation (assume FREE plan)');
      return {
        allowed: true, // Changed from false to true - allow while loading
        remaining: 999,
        limit: 999
      };
    }

    // Hourly plan: check compute hours budget
    // Compute-hours plans (BASIC/STANDARD/PREMIUM today) gate by aggregated
    // compute time, not by job count, regardless of plan_type. Free, API ONLY,
    // and future count-only plans fall through to the legacy gate below.
    if (
      subscription.total_compute_hours !== null &&
      subscription.total_compute_hours !== undefined &&
      subscription.total_compute_hours > 0
    ) {
      const limitSeconds = subscription.total_compute_hours * 3600;
      const usedSeconds = usage.total_compute_seconds ?? 0;
      const remainingSeconds = limitSeconds - usedSeconds;
      const remainingHours = Math.max(0, remainingSeconds / 3600);
      const totalHours = subscription.total_compute_hours;
      setChecking(false);

      return {
        allowed: remainingSeconds > 0,
        remaining: remainingHours,
        limit: totalHours
      };
    }

    // Monthly plan: existing logic
    const limit = subscription.max_processing_jobs_per_month ?? 0;
    const used = usage.processing_jobs_count ?? 0;
    const remaining = limit - used;

    setChecking(false);

    return {
      allowed: remaining > 0,
      remaining,
      limit
    };
  };

  /**
   * Check if user can upload more files
   * @deprecated Use canOperate() instead - now uses shared operations counter
   * @returns QuotaCheckResult with allowed status and remaining quota
   */
  const canUpload = async (): Promise<QuotaCheckResult> => {
    // Redirect to canOperate for shared counter logic
    return canOperate();
  };

  /**
   * Check if user can process more jobs
   * @deprecated Use canOperate() instead - now uses shared operations counter
   * @returns QuotaCheckResult with allowed status and remaining quota
   */
  const canProcess = async (): Promise<QuotaCheckResult> => {
    // Redirect to canOperate for shared counter logic
    return canOperate();
  };

  /**
   * Check if user has storage space available for a file
   *
   * @param fileSizeMB - Size of the file in megabytes
   * @returns QuotaCheckResult with allowed status and remaining quota
   */
  const checkStorage = async (fileSizeMB: number): Promise<QuotaCheckResult> => {
    setChecking(true);

    // IMPORTANT: Allow storage if subscription data is still loading
    // Only block if data is loaded AND limit is reached
    if (!subscription || !usage) {
      setChecking(false);
      console.warn('⚠️ Subscription data not loaded yet, allowing storage (assume FREE plan)');
      return {
        allowed: true, // Changed from false to true - allow while loading
        remaining: 9999,
        limit: 9999
      };
    }

    const limitGB = subscription.max_storage_gb ?? 0;
    const usedGB = usage.storage_used_gb ?? 0;
    const limitMB = limitGB * 1024;
    const usedMB = usedGB * 1024;
    const remainingMB = limitMB - usedMB;

    setChecking(false);

    return {
      allowed: remainingMB >= fileSizeMB,
      remaining: remainingMB,
      limit: limitMB
    };
  };

  /**
   * Get usage percentage for a specific resource type
   *
   * @param type - Resource type (upload, processing, storage)
   * @returns Usage percentage (0-100)
   */
  const getUsagePercentage = (
    type: 'upload' | 'processing' | 'storage' | 'compute_hours'
  ): number => {
    if (!subscription || !usage) return 0;

    switch (type) {
      case 'compute_hours': {
        if (subscription.plan_type !== 'hourly' || !subscription.total_compute_hours) return 0;
        const limitSec = subscription.total_compute_hours * 3600;
        const usedSec = usage.total_compute_seconds ?? 0;
        return limitSec > 0 ? (usedSec / limitSec) * 100 : 0;
      }
      case 'upload': {
        const limit = subscription?.max_uploads_per_month ?? 0;
        const used = usage.uploads_count ?? 0;
        return limit > 0 ? (used / limit) * 100 : 0;
      }
      case 'processing': {
        const limit = subscription?.max_processing_jobs_per_month ?? 0;
        const used = usage.processing_jobs_count ?? 0;
        return limit > 0 ? (used / limit) * 100 : 0;
      }
      case 'storage': {
        const limitGB = subscription?.max_storage_gb ?? 0;
        const usedGB = usage.storage_used_gb ?? 0;
        return limitGB > 0 ? (usedGB / limitGB) * 100 : 0;
      }
      default:
        return 0;
    }
  };

  /**
   * Check if user is approaching quota limit (>80%)
   *
   * @param type - Resource type (upload, processing, storage)
   * @returns True if usage is above 80%
   */
  const isApproachingLimit = (
    type: 'upload' | 'processing' | 'storage' | 'compute_hours'
  ): boolean => {
    const percentage = getUsagePercentage(type);
    return percentage >= 80;
  };

  return {
    canOperate,
    canUpload,
    canProcess,
    checkStorage,
    getUsagePercentage,
    isApproachingLimit,
    checking,
    subscription,
    usage,
    refreshUsage: refreshUserData
  };
};
